/**
 * AggregatrIntegration.ts - Aggregatr integration for Stylr
 * 
 * CHANGE HISTORY:
 * - 251102_1349 danielbeer: Created to fix missing orchestrator integration
 */

/**
 * Initialize Aggregatr integration for Stylr
 * 
 * This sets up workspace-aware storage for styles and preferences
 */
export function initializeAggregatrIntegration(aggregatr: any, _stylrManager: any) {
  if (!aggregatr) {
    console.warn('[Stylr] Aggregatr not available, using in-memory storage');
    return {
      saveStyle: async (style: any) => {
        console.warn('[Stylr] Cannot save style: Aggregatr not available');
        return style;
      },
      loadStyles: async () => {
        console.warn('[Stylr] Cannot load styles: Aggregatr not available');
        return [];
      },
      deleteStyle: async (_styleId: string) => {
        console.warn('[Stylr] Cannot delete style: Aggregatr not available');
        return true;
      }
    };
  }

  console.log('[Stylr] Initializing Aggregatr integration');

  return {
    /**
     * Save a style to Aggregatr
     */
    saveStyle: async (style: any) => {
      console.log('[Stylr] Saving style to Aggregatr', style.id);
      
      try {
        // Save to current workspace
        await aggregatr.set(`stylr:styles:${style.id}`, style, {
          workspace: true, // Workspace-specific
          cache: true, // Enable caching
          index: ['type', 'name', 'gallery'] // Index for search
        });

        return style;
      } catch (error) {
        console.error('[Stylr] Failed to save style to Aggregatr:', error);
        throw error;
      }
    },

    /**
     * Load all styles from Aggregatr
     */
    loadStyles: async () => {
      console.log('[Stylr] Loading styles from Aggregatr');
      
      try {
        // Get all styles from current workspace
        const styles = await aggregatr.query('stylr:styles:*', {
          workspace: true,
          cache: true
        });

        console.log(`[Stylr] Loaded ${styles.length} styles from Aggregatr`);
        return styles;
      } catch (error) {
        console.error('[Stylr] Failed to load styles from Aggregatr:', error);
        return [];
      }
    },

    /**
     * Delete a style from Aggregatr
     */
    deleteStyle: async (_stylrManager: any, styleId: string) => {
      console.log('[Stylr] Deleting style from Aggregatr', styleId);
      
      try {
        await aggregatr.delete(`stylr:styles:${styleId}`, {
          workspace: true
        });

        return true;
      } catch (error) {
        console.error('[Stylr] Failed to delete style from Aggregatr:', error);
        throw error;
      }
    },

    /**
     * Save user preferences to Aggregatr
     */
    savePreferences: async (preferences: any) => {
      console.log('[Stylr] Saving preferences to Aggregatr');
      
      try {
        await aggregatr.set('stylr:preferences', preferences, {
          user: true, // User-specific
          cache: true
        });

        return preferences;
      } catch (error) {
        console.error('[Stylr] Failed to save preferences to Aggregatr:', error);
        throw error;
      }
    },

    /**
     * Load user preferences from Aggregatr
     */
    loadPreferences: async () => {
      console.log('[Stylr] Loading preferences from Aggregatr');
      
      try {
        const preferences = await aggregatr.get('stylr:preferences', {
          user: true,
          cache: true
        });

        return preferences || {};
      } catch (error) {
        console.error('[Stylr] Failed to load preferences from Aggregatr:', error);
        return {};
      }
    },

    /**
     * Search styles using Aggregatr
     */
    searchStyles: async (query: string, filters: any = {}) => {
      console.log('[Stylr] Searching styles in Aggregatr', query);
      
      try {
        const results = await aggregatr.search('stylr:styles:*', {
          query,
          filters,
          workspace: true,
          cache: true
        });

        return results;
      } catch (error) {
        console.error('[Stylr] Failed to search styles in Aggregatr:', error);
        return [];
      }
    },

    /**
     * Save image to Aggregatr
     */
    saveImage: async (image: any) => {
      console.log('[Stylr] Saving image to Aggregatr', image.id);
      
      try {
        await aggregatr.set(`stylr:images:${image.id}`, image, {
          workspace: true,
          cache: true,
          index: ['name', 'folder', 'tags']
        });

        return image;
      } catch (error) {
        console.error('[Stylr] Failed to save image to Aggregatr:', error);
        throw error;
      }
    },

    /**
     * Load all images from Aggregatr
     */
    loadImages: async () => {
      console.log('[Stylr] Loading images from Aggregatr');
      
      try {
        const images = await aggregatr.query('stylr:images:*', {
          workspace: true,
          cache: true
        });

        console.log(`[Stylr] Loaded ${images.length} images from Aggregatr`);
        return images;
      } catch (error) {
        console.error('[Stylr] Failed to load images from Aggregatr:', error);
        return [];
      }
    },

    /**
     * Delete image from Aggregatr
     */
    deleteImage: async (imageId: string) => {
      console.log('[Stylr] Deleting image from Aggregatr', imageId);
      
      try {
        await aggregatr.delete(`stylr:images:${imageId}`, {
          workspace: true
        });

        return true;
      } catch (error) {
        console.error('[Stylr] Failed to delete image from Aggregatr:', error);
        throw error;
      }
    }
  };
}
